import React, {useEffect, useMemo, useState} from 'react';
import {Button, Flex, Popover, Table, TableProps} from "antd";
import {t} from "i18next";
import Title from "antd/es/typography/Title";

import ButtonGrey from "../../ui/buttons/ButtonGrey";
import {DataChannel, DataTotal, Factor} from "../../../api/types";
import IconClose8 from "../../ui/icons/IconClose8";
import IconPinned from "../../ui/icons/IconPinned";
import IconArrowDown from "../../ui/icons/IconArrowDown";
import IconMore from "../../ui/icons/IconMore";
import IconLess from "../../ui/icons/IconLess";
import IconCompareSettings from "../../ui/icons/IconCompareSettings";
import CompareFilter from "./filter/CompareFilter";
import {CaretRightOutlined} from "@ant-design/icons";
import { renderNumberCell } from '../../../numberFormat'
import { HISTORIC_DATA_TABLE } from './dataTotals/historic/HistoricDataTotalTable';

type OnChange = NonNullable<TableProps<DataType>['onChange']>
type GetSingle<T> = T extends (infer U)[] ? U : never
type Sorts = GetSingle<Parameters<OnChange>[2]>

type DataField = 'budget' | 'kpi' | 'roas' | 'cac' | 'kpi_long_term' | 'kpi_short_term'

interface CompareItemsProps {
  dataTotals: DataTotal[],
  historicDataTotal: DataTotal | undefined,
  selectedForComparesIds: string[],
  deleteFromCompares: Function,
  close: Function,
}

interface DataColumnsType {
  budget: string,
  cac: string,
  kpi: string,
  kpi_long_term: string,
  kpi_short_term: string,
  roas: string,
}

interface DataType extends DataColumnsType {
  headertype: string,
}

const mediaType = 'media';
const nonMediaType = 'nonMedia';

const epsilon = 0.01;

export default function CompareItems({
                                       dataTotals,
                                       deleteFromCompares,
                                       selectedForComparesIds,
                                       close,
                                       historicDataTotal,
                                     }: CompareItemsProps) {
  const [selectedMetrics, setSelectedMetrics] = useState<Array<keyof DataColumnsType>>([
    'kpi', 'budget', 'roas',
  ]);
  const [compareItems, setCompareItems] = useState<DataTotal[]>([]);
  const [sorter, setSorter] = useState<Sorts>({});
  const [isMediaExpanded, setIsMediaExpanded] = useState<boolean>(true);
  const [isNonMediaExpanded, setIsNonMediaExpanded] = useState<boolean>(false);


  useEffect(() => {
    if (historicDataTotal) {
      setCompareItems((prevState) => ([
        historicDataTotal,
        ...prevState,
      ]))
    }
  }, [historicDataTotal]);

  useEffect(() => {
    if (dataTotals) {
      setCompareItems(dataTotals);
    }
  }, [dataTotals]);

  useEffect(() => {
    let compares: DataTotal[] = [];

    if (historicDataTotal && selectedForComparesIds.includes(HISTORIC_DATA_TABLE)) {
      compares.push(historicDataTotal);
    }

    if (dataTotals) {
      const filteredDataTotals = dataTotals
        .filter((dataTotal: DataTotal) => selectedForComparesIds.includes(dataTotal["@id"]));

      compares = [...compares, ...filteredDataTotals];
    }

    setCompareItems(compares);
  }, [historicDataTotal, dataTotals, selectedForComparesIds]);

  const isFirstItem = (id: string) => compareItems[0]?.["@id"] === id;

  const setFirst = (compareItem: DataTotal) => {
    const compareItemIndex = compareItems.findIndex(item => item["@id"] === compareItem["@id"]);

    if (compareItemIndex) {
      const currentCompareItems = Array.from(compareItems);
      currentCompareItems.splice(compareItemIndex, 1);
      currentCompareItems.unshift(compareItem);
      setCompareItems(currentCompareItems)
    }
  };

  const columns: TableProps<DataType>['columns'] = useMemo(() => {
    const columns: TableProps<DataType>['columns'] = [
      {
        title: t('planner.model'),
        dataIndex: 'channel',
        key: 'channel',
        fixed: 'left',
        width: 300,
      }
    ];

    for (let compareItem of compareItems) {
      columns.push({
        title: () =>
          <Flex style={{width: '100%'}} gap={'small'} align={'center'} justify={'space-between'}>
            <Flex gap={4}>
              <span className={'variant-table__title'}>{compareItem.name}</span>
            </Flex>
            <Flex align={'center'}>
              {isFirstItem(compareItem["@id"]) &&
                <IconPinned/>
              }
              {!isFirstItem(compareItem["@id"]) &&
                <Popover placement="bottomRight" content={
                  <div className={'compare__plan-popover'}>
                    <div
                      className={'compare__plan-popover-item compare__plan-popover-item_colored'}
                      onClick={() => setFirst(compareItem)}
                    >
                      {t('planner.setPrimary')}
                    </div>
                    <div
                      className={'compare__plan-popover-item'}
                      onClick={() => {
                        deleteFromCompares(compareItem["@id"])
                      }}
                    >
                      Не сравнивать
                    </div>
                  </div>
                } arrow={false}>
                  <Button type="text" icon={<IconArrowDown/>}/>
                </Popover>
              }
            </Flex>
          </Flex>,
        fixed: isFirstItem(compareItem["@id"]) ? 'left' : false,
        className: isFirstItem(compareItem["@id"]) ? 'compare__fixed-cell' : '',
        children: [
          {
            title: <Popover placement="top" content={
              <div>{t('planner.kpiInTotalsFull')}</div>
            } arrow overlayClassName={'sider-links__popover'}>
              {t('planner.kpiInTotals')}
            </Popover>,
            dataIndex: 'kpi' + compareItem["@id"],
            key: 'kpi_' + compareItem["@id"],
            width: 160,
            fixed: isFirstItem(compareItem["@id"]) ? 'left' : false,
            className: isFirstItem(compareItem["@id"]) ? 'compare__fixed-cell' : '',
            hidden: !selectedMetrics.includes('kpi'),
            sorter: true,
          },
          {
            title: t('planner.budget'),
            dataIndex: 'budget' + compareItem["@id"],
            key: 'budget_' + compareItem["@id"],
            width: 160,
            fixed: isFirstItem(compareItem["@id"]) ? 'left' : false,
            className: isFirstItem(compareItem["@id"]) ? 'compare__fixed-cell' : '',
            hidden: !selectedMetrics.includes('budget'),
            sorter: true,
          },
          {
            title: t('planner.roas'),
            dataIndex: 'roas' + compareItem["@id"],
            key: 'roas_' + compareItem["@id"],
            width: 160,
            fixed: isFirstItem(compareItem["@id"]) ? 'left' : false,
            className: isFirstItem(compareItem["@id"]) ? 'compare__fixed-cell' : '',
            hidden: !selectedMetrics.includes('roas'),
            sorter: true,
          },
          {
            title: <Popover placement="top" content={
              <div>{t('planner.kpiShortTermFull')}</div>
            } arrow overlayClassName={'sider-links__popover'}>
              {t('planner.kpiShortTerm')}
            </Popover>,
            dataIndex: 'kpi_short_term' + compareItem["@id"],
            key: 'kpi_short_term_' + compareItem["@id"],
            width: 160,
            fixed: isFirstItem(compareItem["@id"]) ? 'left' : false,
            className: isFirstItem(compareItem["@id"]) ? 'compare__fixed-cell' : '',
            hidden: !selectedMetrics.includes('kpi_short_term'),
            sorter: true,
          },
          {
            title: <Popover placement="top" content={
              <div>{t('planner.kpiLongTermFull')}</div>
            } arrow overlayClassName={'sider-links__popover'}>
              {t('planner.kpiLongTerm')}
            </Popover>,
            dataIndex: 'kpi_long_term' + compareItem["@id"],
            key: 'kpi_long_term_' + compareItem["@id"],
            width: 160,
            fixed: isFirstItem(compareItem["@id"]) ? 'left' : false,
            className: isFirstItem(compareItem["@id"]) ? 'compare__fixed-cell' : '',
            hidden: !selectedMetrics.includes('kpi_long_term'),
            sorter: true,
          },
        ]
      })
    }
    return columns
  }, [compareItems, isFirstItem, selectedMetrics, sorter]);

  const getDiffByFirst = (key: keyof DataColumnsType, channelName: string, value: number) => {
    const firstCompareItem = compareItems[0];
    const firstCompareItemChannel = firstCompareItem.dataChannels.find(dataChannel => dataChannel.name === channelName);

    if (!firstCompareItemChannel) {
      return 0
    }

    const firstItemValue = Number(firstCompareItemChannel?.metrics[key]) || 0;

    if (parseFloat(value.toFixed(2)) === parseFloat(firstItemValue.toFixed(2))) {
      return 0
    }

    return Math.abs(value) - Math.abs(firstItemValue)
  };

  const getTotalDiffByFirst = (key: keyof DataColumnsType, value: number) => {
    const firstCompareTotal = compareItems[0].metrics.total;
    const firstCompareTotalValue = firstCompareTotal[key];

    if (parseFloat(value.toFixed(2)) === parseFloat(firstCompareTotalValue.toFixed(2))) {
      return 0
    }

    return Math.abs(value) - Math.abs(firstCompareTotalValue)
  };

  const getCell = (value: any, key: keyof DataColumnsType, channelName: string, compareItem: DataTotal, precision: number = 0) => {
    if (value === null || value === undefined) {
      return renderNumberCell(null, precision);
    }

    if (isFirstItem(compareItem["@id"])) {
      return renderNumberCell(value, precision)
    }

    let suffix = <div></div>;

    if (getDiffByFirst(key, channelName, Number(value) || 0) === 0) {
      suffix = <div></div>
    } else if (getDiffByFirst(key, channelName, value) > epsilon) {
      suffix = <IconMore/>
    } else if (getDiffByFirst(key, channelName, value) < epsilon) {
      suffix = <IconLess/>
    }

    return <Flex gap={4} align={'center'} wrap justify={'flex-end'}>{suffix}{renderNumberCell(value, precision)}</Flex>
  };

  const getTotalCell = (value: number, key: keyof DataColumnsType, compareItem: DataTotal, precision: number = 0) => {
    if (isFirstItem(compareItem["@id"])) {
      return renderNumberCell(value, precision)
    }

    let suffix = <div></div>;

    if (getTotalDiffByFirst(key, value) === 0) {
      suffix = <div></div>
    } else if (getTotalDiffByFirst(key, value) > epsilon) {
      suffix = <IconMore/>
    } else if (getTotalDiffByFirst(key, value) < epsilon) {
      suffix = <IconLess/>
    }

    return <Flex gap={4} align={'center'} justify={'flex-end'}>{suffix}{renderNumberCell(value, precision)}</Flex>
  };

  const data = useMemo(() => {
    const result: any[] = [];
    //const channels = engine?.channels || [];

    const totalData: any = {
      channel: t('planner.total'),
    };
    for (let compareItem of compareItems) {
      totalData['budget' + compareItem["@id"]] = getTotalCell(Number(compareItem?.metrics?.total?.budget), 'budget', compareItem);
      totalData['cac' + compareItem["@id"]] = getTotalCell(Number(compareItem?.metrics?.total?.cac), 'cac', compareItem);
      totalData['roas' + compareItem["@id"]] = getTotalCell(Number(compareItem?.metrics?.total?.roas), 'roas', compareItem, 1);
      totalData['kpi' + compareItem["@id"]] = getTotalCell(Number(compareItem?.metrics?.total?.kpi), 'kpi', compareItem);
      totalData['kpi_short_term' + compareItem["@id"]] = '-';
      totalData['kpi_long_term' + compareItem["@id"]] = '-';
      totalData.id = 'total';
      totalData.isTotal = true
    }

    result.push(totalData);

    let dataChannels: DataChannel[] = [];
    let factors: Factor[] = [];

    if (sorter.columnKey && sorter.order) {
      const key: DataField = String(sorter.columnKey).split('_').shift() as DataField;
      const compareItemId = String(sorter.columnKey).split('_').slice(1).join('_');
      const order = String(sorter.order);

      let compareItem = compareItems.find((compareItem: DataTotal) => compareItem["@id"] === compareItemId);
      dataChannels = (compareItem?.dataChannels || []).sort((a, b) =>
        order === 'ascend' ? (Number(a.metrics[key]) - Number(b.metrics[key])) : (Number(b.metrics[key]) - Number(a.metrics[key])));

      factors = (compareItem?.nonMediaDataFactors || []).sort((a, b) =>
        order === 'ascend' ? (Number(a.metrics[key]) - Number(b.metrics[key])) : (Number(b.metrics[key]) - Number(a.metrics[key])))
        .map((nonMediaFactor) => nonMediaFactor.factor);
    } else {
      dataChannels = compareItems[0]?.dataChannels || [];
      factors = compareItems[0]?.nonMediaDataFactors.map((nonMediaFactor) => nonMediaFactor.factor) || [];
    }

    const channels = dataChannels.map((dataChannel) => dataChannel.channel) || [];

    if (channels.length > 0) {
      const mediaGroup: any = {
        channel: (
          <Flex
            justify={'space-between'}
            align={'center'}
            className={'data-total__group-title' + (isMediaExpanded ? ' expanded' : '')}
            onClick={() => setIsMediaExpanded(!isMediaExpanded)}
          >
            <Flex>{t('planner.media')} ({channels.length})</Flex>
            <Flex>
            <span style={{ color: '#7E8681' }}>
              <CaretRightOutlined rotate={isMediaExpanded ? 90 : 0} />
            </span>
            </Flex>
          </Flex>
        ),
        id: 'media_group',
        isTotal: false,
        headertype: mediaType,
      };

      for (let compareItem of compareItems) {
        mediaGroup['budget' + compareItem["@id"]] = isMediaExpanded ? '' : getCell(
          compareItem?.metrics?.media?.budget ?? compareItem?.metrics?.total?.budget,
          'budget',
          '',
          compareItem
        );
        mediaGroup['cac' + compareItem["@id"]] = isMediaExpanded ? '' : getCell(
          compareItem?.metrics?.media?.cac ?? compareItem?.metrics?.total?.cac,
          'cac',
          '',
          compareItem
        );
        mediaGroup['roas' + compareItem["@id"]] = isMediaExpanded ? '' : getCell(
          compareItem?.metrics?.media?.roas ?? compareItem?.metrics?.total?.roas,
          'roas',
          '',
          compareItem,
          1
        );
        mediaGroup['kpi' + compareItem["@id"]] = isMediaExpanded ? '' : getCell(
          compareItem?.metrics?.media?.kpi ?? compareItem?.metrics?.total?.kpi,
          'kpi',
          '',
          compareItem
        );
        mediaGroup['kpi_short_term' + compareItem["@id"]] = isMediaExpanded ? '' : getCell(
          compareItem?.metrics?.media?.kpi_short_term,
          'kpi_short_term',
          '',
          compareItem
        );
        mediaGroup['kpi_long_term' + compareItem["@id"]] = isMediaExpanded ? '' : getCell(
          compareItem?.metrics?.media?.kpi_long_term,
          'kpi_long_term',
          '',
          compareItem
        );
        mediaGroup['headertype:'] = '';
      }

      result.push(mediaGroup);
    }

    if (isMediaExpanded) {
      for (let channel of channels) {
        const row: any = {
          channel: channel.name,
        };

        for (let compareItem of compareItems) {
          let channelData = compareItem.dataChannels.find(
            (ch) => ch.channel.id === channel.id && (compareItem["@type"] === 'HistoricDataTotalDto' || channel.isActive)
          );

          if (channelData) {
            row['budget' + compareItem["@id"]] = getCell(channelData?.metrics?.budget, 'budget', channelData.name, compareItem);
            row['cac' + compareItem["@id"]] = getCell(channelData?.metrics?.cac, 'cac', channelData.name, compareItem);
            row['roas' + compareItem["@id"]] = getCell(channelData?.metrics?.roas, 'roas', channelData.name, compareItem, 1);
            row['kpi' + compareItem["@id"]] = getCell(channelData?.metrics?.kpi, 'kpi', channelData.name, compareItem);
            row['kpi_short_term' + compareItem["@id"]] = getCell(channelData?.metrics?.kpi_short_term, 'kpi_short_term', channelData.name, compareItem);
            row['kpi_long_term' + compareItem["@id"]] = getCell(channelData?.metrics?.kpi_long_term, 'kpi_long_term', channelData.name, compareItem);
            row.id = channel.id;
            row.isTotal = false;
          }
        }
        result.push(row);
      }

      const allMediaRow: any = {
        channel: t('planner.allMedia'),
        id: 'total_media',
        isTotal: false,
        headertype: '',
      };

      for (let compareItem of compareItems) {
        allMediaRow['budget' + compareItem["@id"]] = getCell(
          compareItem?.metrics?.media?.budget ?? compareItem?.metrics?.total?.budget,
          'budget',
          '',
          compareItem
        );
        allMediaRow['cac' + compareItem["@id"]] = getCell(
          compareItem?.metrics?.media?.cac ?? compareItem?.metrics?.total?.cac,
          'cac',
          '',
          compareItem
        );
        allMediaRow['roas' + compareItem["@id"]] = getCell(
          compareItem?.metrics?.media?.roas ?? compareItem?.metrics?.total?.roas,
          'roas',
          '',
          compareItem,
          1,
        );
        allMediaRow['kpi' + compareItem["@id"]] = getCell(
          compareItem?.metrics?.media?.kpi ?? compareItem?.metrics?.total?.kpi,
          'kpi',
          '',
          compareItem
        );
        allMediaRow['kpi_short_term' + compareItem["@id"]] = getCell(
          compareItem?.metrics?.media?.kpi_short_term,
          'kpi_short_term',
          '',
          compareItem
        );
        allMediaRow['kpi_long_term' + compareItem["@id"]] = getCell(
          compareItem?.metrics?.media?.kpi_long_term,
          'kpi_long_term',
          '',
          compareItem
        );
      }
      result.push(allMediaRow);
    }

    if (factors.length > 0) {
      const data: any = {
        channel: <Flex
          justify={'space-between'}
          align={'center'}
          className={'data-total__group-title' + (isNonMediaExpanded ? ' expanded' : '')}
          onClick={() => setIsNonMediaExpanded(!isNonMediaExpanded)}
        >
          <Flex>{t('planner.nonMedia')} ({factors.length})</Flex>
          <Flex>
            <span style={{color: '#7E8681'}}><CaretRightOutlined rotate={isNonMediaExpanded ? 90 : 0}/></span>
          </Flex>
        </Flex>,
        id: 'non_media_group',
        isTotal: false,
        headertype: nonMediaType,
      };

      for (let compareItem of compareItems) {
        data['budget' + compareItem['@id']] = isNonMediaExpanded ? '' : getCell(compareItem?.metrics?.nonMedia?.budget, 'budget', '', compareItem)
        data['cac' + compareItem['@id']] = isNonMediaExpanded ? '' : getCell(compareItem?.metrics?.nonMedia?.cac, 'cac', '', compareItem)
        data['roas' + compareItem['@id']] = isNonMediaExpanded ? '' : getCell(compareItem?.metrics?.nonMedia?.roas, 'roas', '', compareItem, 1)
        data['kpi' + compareItem['@id']] = isNonMediaExpanded ? '' : getCell(compareItem?.metrics?.nonMedia?.kpi, 'kpi', '', compareItem)
        data['kpi_short_term' + compareItem['@id']] = isNonMediaExpanded ? '' : getCell(compareItem?.metrics?.nonMedia?.kpi_short_term, 'kpi_short_term', '', compareItem)
        data['kpi_long_term' + compareItem['@id']] = isNonMediaExpanded ? '' : getCell(compareItem?.metrics?.nonMedia?.kpi_long_term, 'kpi_long_term', '', compareItem)
      }
      result.push(data);
    }

    if (isNonMediaExpanded) {
      for (let factor of factors) {
        const data: any = {
          channel: factor.name,
        };

        for (let compareItem of compareItems) {
          const factorData = compareItem.nonMediaDataFactors.find(nonMediaDataFactor => nonMediaDataFactor.factor.id === factor.id);

          if (factorData) {
            data['budget' + compareItem["@id"]] = getCell(factorData?.metrics?.budget, 'budget', factorData.factor.name, compareItem);
            data['cac' + compareItem["@id"]] = getCell(factorData?.metrics?.cac, 'cac', factorData.factor.name, compareItem);
            data['roas' + compareItem["@id"]] = getCell(factorData?.metrics?.roas, 'roas', factorData.factor.name, compareItem, 1);
            data['kpi' + compareItem["@id"]] = getCell(factorData?.metrics?.kpi, 'kpi', factorData.factor.name, compareItem);
            data['kpi_short_term' + compareItem["@id"]] = getCell(factorData?.metrics?.kpi_short_term, 'kpi_short_term', factorData.factor.name, compareItem);
            data['kpi_long_term' + compareItem["@id"]] = getCell(factorData?.metrics?.kpi_long_term, 'kpi_long_term', factorData.factor.name, compareItem);
            data.id = factor["@id"];
            data.isTotal = false;
            data.headertype = '';
          }
        }
        result.push(data)
      }

      const data: any = {
        channel: t('planner.allNonMedia'),
        id: 'total_non_media',
        isTotal: false,
        headertype: '',
      };

      for (let compareItem of compareItems) {
        data['budget' + compareItem["@id"]] = getCell(compareItem?.metrics?.nonMedia?.budget, 'budget', '', compareItem);
        data['cac' + compareItem["@id"]] = getCell(compareItem?.metrics?.nonMedia?.cac, 'cac', '', compareItem);
        data['roas' + compareItem["@id"]] = getCell(compareItem?.metrics?.nonMedia?.roas, 'roas', '', compareItem, 1);
        data['kpi' + compareItem["@id"]] = getCell(compareItem?.metrics?.nonMedia?.kpi, 'kpi', '', compareItem);
        data['kpi_short_term' + compareItem["@id"]] = getCell(compareItem?.metrics?.nonMedia?.kpi_short_term, 'kpi_short_term', '', compareItem);
        data['kpi_long_term' + compareItem["@id"]] = getCell(compareItem?.metrics?.nonMedia?.kpi_long_term, 'kpi_long_term', '', compareItem);
      }
      result.push(data);
    }

    return result
  }, [compareItems, selectedForComparesIds, sorter, isNonMediaExpanded, isMediaExpanded]);

  const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra) => {
    setSorter(sorter as Sorts)
  };

  return (
    <Flex vertical gap={'middle'} justify={'center'} className={'compare__items'}>
      <Flex justify={'space-between'}>
        <Flex align={'center'} gap={'large'}>
          <Title level={4} className={'planner__block-title'}>{t('planner.comparing')} ({compareItems.length})</Title>
          <Popover placement="bottomLeft" content={
            <div className={'compare__filter-popover'}>
              <CompareFilter
                selectedMetrics={selectedMetrics}
                setSelectedMetrics={setSelectedMetrics}
              />
            </div>
          } arrow={false}>
            <Flex align={'center'} gap={'small'}>
              <IconCompareSettings/>
              {t('planner.setView')}
            </Flex>
          </Popover>
        </Flex>

        <Flex gap={'large'} align={'center'}>
          <ButtonGrey
            onClick={() => close()}
            style={{
              color: '#384146',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '18px',
              background: '#F3F6F8',
            }}
            icon={<IconClose8/>}
            size={'middle'}
          >{t('planner.close')}</ButtonGrey>
        </Flex>
      </Flex>
      <Flex justify={'space-between'}>
        <Table
          className={`variant-table`}
          style={{maxWidth: '100%'}}
          columns={columns}
          dataSource={data}
          onChange={onChange}
          pagination={false}
          bordered
          rowKey={'id'}
          scroll={{x: 800}}
          size={'middle'}
          showSorterTooltip={false}
        />
      </Flex>
    </Flex>
  )
}
